import React, { useEffect, useRef } from "react";

import { useMutation } from "graphql-hooks";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { Mutations } from "@api/graphql/queries/Mutations";
import { AnyProduct, cartAddProduct, cartAddProductVariables, cartRemove, cartRemoveVariables } from "@api/graphql/types";
import { NormalProduct, productIsBundle, productIsDigital, productIsService } from "@api/graphql/typeHelpers";
import { Badge } from "@components/Badge";
import { Button, QuantityButton } from "@components/Button";
import { Card } from "@components/Card/Card";
import { Gift, OriginalPrice, Price, Saving } from "@components/Card/Elements";
import { DigitalImageFrame } from "@components/DigitalImageFrame";
import { SVGArrowLeft } from "@components/SVGCollection";
import { Typography } from "@components/Typography";
import { useCart } from "@hooks/useCart";
import { CartActions } from "@redux/actions/CartActions";
import { ApplicationState } from "@redux/reducers";
import { bp } from "@styles/theme";
import { ImageUtils } from "@utils/ImageUtils";
import { Path } from "@utils/Path";
import { StringUtils } from "@utils/StringUtils";
import { useLoctool } from "@bigfish/react-loctool";
import { CURRENCY_FORMAT_OPTIONS } from "@utils/Constants";
import { HtmlUtils } from "@utils/HtmlUtils";

interface AccordionCardProps {
    product: AnyProduct;
    open?: boolean;
    group?: string;
    className?: string;
    handleModal: (product: NormalProduct) => void;
    onImageClick?: () => void;
}

export const AccordionCard = ({ product, open, group, className, handleModal, onImageClick }: AccordionCardProps) => {
    const Intl = useLoctool();
    const router = useRouter();
    const dispatch = useDispatch();
    const items = useSelector((state: ApplicationState) => state.cart.allItems);
    const { items: serviceItems } = useSelector((state: ApplicationState) => state.cart.serviceCart);
    const isItemInCart = items.some(item => item.product.id === product.id);
    const isServiceItemInCart = serviceItems.some(item => item.product.id === product.id);
    const itemQuantity = items.filter(x => x.product.id === product.id).length;
    const { addProduct, removeLastAddedProduct, loading } = useCart();
    const [addProductMutation, { loading: addProductLoading }] = useMutation<cartAddProduct, cartAddProductVariables>(Mutations.cartAddProduct);
    const [removeProduct] = useMutation<cartRemove, cartRemoveVariables>(Mutations.cartRemove);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const accordionContentRef = useRef<HTMLDivElement | null>(null);

    const addServiceToCart = async (product_id: number) => {
        if (serviceItems.length > 0) {
            const result = await removeProduct({ variables: { cart_item_id: serviceItems[0].id } });
            const cart = result.data?.cartRemove;
            if (cart) {
                dispatch(CartActions.updateCart(cart));
            }
        }
        const result = await addProductMutation({ variables: { product_id } });
        const cart = result.data?.cartAddProduct;
        if (cart) {
            dispatch(CartActions.updateCart(cart));
            router.push(Path.serviceCheckoutStart);
        }
    };

    useEffect(() => {
        if (open) {
            cardRef?.current?.classList.add("isOpen");

            if (accordionContentRef.current !== null) {
                accordionContentRef.current.style.maxHeight = `${accordionContentRef.current.scrollHeight}px`;
            }
        }
    });

    const toggleAccordion = () => {
        const cardClassList = cardRef?.current?.classList;
        const openAccordions = document.querySelectorAll(`.${group}.isOpen`);

        if (cardClassList?.contains("isOpen")) {
            cardClassList.remove("isOpen");

            if (accordionContentRef.current) {
                accordionContentRef.current.style.maxHeight = "0px";
            }
        } else if (group && cardClassList?.contains(group) && !cardClassList.contains("isOpen") && openAccordions !== null) {
            cardClassList.add("isOpen");

            if (accordionContentRef.current) {
                accordionContentRef.current.style.maxHeight = `${accordionContentRef.current.scrollHeight}px`;
            }

            openAccordions.forEach(openAccordion => {
                const accordionBody = openAccordion.querySelector<HTMLDivElement>(".accordion-card_content");

                openAccordion.classList.remove("isOpen");

                if (accordionBody !== null) {
                    accordionBody.style.maxHeight = "0px";
                }
            });
        } else {
            cardClassList?.add("isOpen");

            if (accordionContentRef.current) {
                accordionContentRef.current.style.maxHeight = `${accordionContentRef.current.scrollHeight}px`;
            }
        }

        accordionContentRef?.current?.addEventListener("transitionstart", () => {
            cardClassList?.add("opening");
        });

        accordionContentRef?.current?.addEventListener("transitionend", () => {
            cardClassList?.remove("opening");
        });
    };

    const handleAdd = async (product: NormalProduct, withModal: boolean) => {
        await addProduct(product.id);
        if (withModal) {
            handleModal(product);
        }
    };

    const handleRemove = async (product: NormalProduct) => {
        await removeLastAddedProduct(product.id);
    };

    const parsedStandardPrice = "standard_price" in product && product.standard_price ? product.standard_price : 0;
    const discountPercent = StringUtils.discountPercent(parsedStandardPrice, product.discount_price, product.price.unit_price);
    const hasStandardPrice =
        "standard_price" in product &&
        product.standard_price &&
        product.standard_price !== 0 &&
        product.standard_price > (product.discount_price ?? product.price.unit_price);

    return (
        <StyledAccordionCard
            onClick={product.text_more ? toggleAccordion : undefined}
            ref={cardRef}
            className={`accordion ${group} ${className}`}
            data-ce-measure-widget={HtmlUtils.removeTags(product.name)}
        >
            <AccordionCardImage
                onClick={e => {
                    if (onImageClick) {
                        e.stopPropagation();
                        onImageClick();
                    }
                }}
            >
                {productIsDigital(product) ? (
                    <ProductDigitalImage image={ImageUtils.productImageWithFallback(product, { width: 100 })} alternate={HtmlUtils.removeTags(product.name)} />
                ) : productIsService(product) ? (
                    <ProductPrintImage {...ImageUtils.serviceImageWithFallback(product, { width: 100 })} alt={HtmlUtils.removeTags(product.name)} />
                ) : productIsBundle(product) ? (
                    <ProductPrintImage {...ImageUtils.bundleImageWithFallback(product, { width: 300 })} alt={HtmlUtils.removeTags(product.name)} />
                ) : (
                    <ProductPrintImage {...ImageUtils.productImageWithFallback(product, { width: 100 })} alt={HtmlUtils.removeTags(product.name)} />
                )}
                {product.is_badge_discount && discountPercent > 0 && <AccordionCardBadge text={`-${discountPercent}%`} color="red" />}
            </AccordionCardImage>
            <AccordionCardHeader>
                <div>
                    {product.text_featured && <UpTitle>{product.text_featured}</UpTitle>}
                    <Title>
                        <div dangerouslySetInnerHTML={{ __html: HtmlUtils.sanitize(product.name) }} />
                    </Title>
                </div>
                {product.text_more && (
                    <AccordionCardToggle>
                        <SVGArrowLeft />
                    </AccordionCardToggle>
                )}
            </AccordionCardHeader>
            <div>
                {product.text_gift && (
                    <Gift>
                        <GiftText>{product.text_gift}</GiftText>
                    </Gift>
                )}
            </div>
            <AccordionCardContent ref={accordionContentRef} className="accordion-card_content">
                {product.text_more && (
                    <SubscriptionDetails>
                        {product.text_more}
                        {product.external_url && (
                            <a
                                onClick={(event: React.MouseEvent) => {
                                    event.stopPropagation();
                                }}
                                href={product.external_url}
                            >
                                {Intl.formatMessage({ id: "common.learnMore" })}
                            </a>
                        )}
                    </SubscriptionDetails>
                )}
            </AccordionCardContent>
            <AccordionCardAction>
                {"standard_price" in product && product.standard_price && hasStandardPrice && (
                    <AccordionCardOriginalPrice>{Intl.formatNumber(product.standard_price, CURRENCY_FORMAT_OPTIONS)}</AccordionCardOriginalPrice>
                )}

                <AccordionCardPrice>{Intl.formatNumber(product.discount_price ?? product.price.unit_price, CURRENCY_FORMAT_OPTIONS)}</AccordionCardPrice>
                {discountPercent >= 5 && (
                    <AccordionCardSaving>{Intl.formatMessage({ id: "pages.home.wideProductCard.saving" }, { saving: discountPercent })}</AccordionCardSaving>
                )}
                {productIsService(product) ? (
                    <AccordionCardActionButton
                        disabled={addProductLoading || isServiceItemInCart}
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            addServiceToCart(product.id);
                        }}
                    >
                        {isServiceItemInCart ? Intl.formatMessage({ id: "pages.product.inCart" }) : Intl.formatMessage({ id: "pages.product.buyNow" })}
                    </AccordionCardActionButton>
                ) : (
                    <>
                        {!isItemInCart && (
                            <AccordionCardActionButton
                                disabled={loading}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleAdd(product, true);
                                }}
                            >
                                {Intl.formatMessage({ id: "pages.home.wideProductCard.addToCart" })}
                            </AccordionCardActionButton>
                        )}
                        {isItemInCart && itemQuantity && (
                            <QuantityWrapper $disabled={loading}>
                                <QuantityButton
                                    name="productQuantity"
                                    max={10}
                                    quantity={itemQuantity}
                                    onAdd={() => handleAdd(product, false)}
                                    onRemove={() => handleRemove(product)}
                                />
                            </QuantityWrapper>
                        )}
                    </>
                )}
            </AccordionCardAction>
        </StyledAccordionCard>
    );
};

const GiftText = styled.p`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AccordionCardImage = styled.div`
    grid-area: image;
    position: relative;
`;

const ProductPrintImage = styled.img`
    width: 100px;
    height: auto;
    margin-right: 26px;
    object-fit: contain;
    object-position: top left;
    transition: all 300ms;
`;

const ProductDigitalImage = styled(DigitalImageFrame)`
    width: 100px;
    height: auto;
    margin-right: 26px;
    transition: all 300ms;
`;

const AccordionCardBadge = styled(Badge)`
    position: absolute;
    bottom: 0;
    left: -20px;
    z-index: 2;
`;

const AccordionCardHeader = styled.div`
    grid-area: header;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: start;
`;

const UpTitle = styled(Typography.UpTitle)`
    font-size: 20px;
    line-height: 32px;
    margin-right: 20px;
    margin-bottom: 10px;

    ${bp.large} {
        font-size: 25px;
        line-height: 40px;
    }
`;

const Title = styled(Typography.H3)`
    margin-right: 20px;
    margin-bottom: 0;
    font-size: 17px;
    line-height: 22px;

    ${bp.large} {
        font-size: 22px;
        line-height: 30px;
    }
`;

const AccordionCardContent = styled.div`
    grid-area: hideContent;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transition: max-height 300ms, opacity 300ms;
`;

const SubscriptionDetails = styled.div`
    font-size: 14px;
    line-height: 24px;
    padding-bottom: 16px;
    margin-top: 10px;

    a {
        display: block;
        font-weight: 700;
        text-decoration: underline;
    }
`;

const AccordionCardAction = styled.div`
    grid-area: action;
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas:
        "originalPrice price"
        "button button"
        "saving saving";
    align-items: baseline;
    justify-items: end;
    width: max-content;
    margin-left: auto;
`;

const AccordionCardOriginalPrice = styled(OriginalPrice)`
    grid-area: originalPrice;
`;

const AccordionCardPrice = styled(Price)`
    grid-area: price;
`;

const AccordionCardSaving = styled(Saving)`
    grid-area: saving;
`;

const AccordionCardActionButton = styled(Button.Primary)<{ onClick?: void }>`
    grid-area: button;
    margin: 8px 0;
`;

const AccordionCardToggle = styled.button`
    color: ${props => props.theme.color.red};

    svg {
        width: 12px;
        transition: transform 300ms;
        transform: rotate(-90deg);
    }
`;

const StyledAccordionCard = styled(Card)<{ $isOpen?: boolean; $openDefault?: boolean }>`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-areas:
        "image header"
        "image content"
        "hideContent hideContent"
        "action action";
    align-items: flex-start;
    position: relative;
    width: 100%;
    cursor: pointer;

    ${bp.medium} {
        grid-template-columns: auto 1fr auto;
        grid-template-rows: auto 90px auto auto;
        grid-template-areas:
            "image header header"
            "image content action"
            ". hideContent hideContent";

        ${Gift} {
            margin-right: 20px;
        }
    }

    &.isOpen {
        ${AccordionCardContent} {
            opacity: 1;
            transition: max-height 300ms, opacity 300ms;
        }

        ${AccordionCardToggle} {
            svg {
                transform: rotate(90deg);
            }
        }
    }
`;

const QuantityWrapper = styled.div<{ $disabled: boolean }>`
    grid-area: button;
    margin: 8px 0;
    z-index: 1;

    ${props =>
        props.$disabled === true &&
        `
        opacity: 0.6;

        button {
            pointer-events: none;
        }
    `}
`;
